(() => {
  'use strict';
  const marker = 'loop-checkout-v1';

  function eligible(cart, config) {
    return cart.currency === 'EUR'
      && cart.items.some(item => Number(item.product_id) === config.tagProduct && item.quantity > 0)
      && !cart.items.some(item => Number(item.product_id) === config.loopProduct && item.quantity > 0);
  }

  function codes(cart) {
    const applications = [...(cart.cart_level_discount_applications || [])];
    for (const item of cart.items) {
      for (const allocation of item.line_level_discount_allocations || []) applications.push(allocation.discount_application);
    }
    return [...new Set([
      ...(cart.discount_codes || []).map(discount => discount.code),
      ...applications.filter(discount => discount.type === 'discount_code').map(discount => discount.title)
    ].filter(Boolean))];
  }

  function identity(item) {
    return JSON.stringify([item.variant_id || item.id, item.quantity, Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan?.id || null]);
  }

  function itemsPreserved(before, after) {
    return after.length === before.length
      && before.every(item => after.some(next => identity(item) === identity(next) && next.final_line_price <= item.final_line_price));
  }

  function verified(before, after, config, variantId) {
    const loops = after.items.filter(item => Number(item.product_id) === config.loopProduct);
    if (after.currency !== 'EUR' || loops.length !== 1) return false;
    const loop = loops[0];
    if (Number(loop.variant_id || loop.id) !== variantId || loop.quantity !== 1 || loop.final_line_price !== 199 || loop.properties?._EA_Offer !== marker) return false;
    const others = after.items.filter(item => Number(item.product_id) !== config.loopProduct);
    return itemsPreserved(before.items, others)
      && after.total_price <= before.total_price + 199;
  }

  async function accept(request, config, variantId) {
    const before = await request('cart.js');
    if (!eligible(before, config)) throw new Error('changed');
    await request('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { _EA_Offer: marker } }] });
    const originalCodes = codes(before);
    const after = await request('cart/update.js', { discount: [...new Set([...originalCodes, config.code])].join(',') });
    if (verified(before, after, config, variantId)) return after;

    const added = after.items.filter(item => Number(item.variant_id || item.id) === variantId && item.properties?._EA_Offer === marker);
    if (added.length !== 1 || added[0].quantity !== 1) throw new Error('uncertain');
    await request('cart/change.js', { id: added[0].key, quantity: 0 });
    const retainedCodes = [...new Set([...originalCodes, ...codes(after).filter(code => code !== config.code)])];
    const restored = await request('cart/update.js', { discount: retainedCodes.join(',') });
    if (restored.currency !== before.currency || !itemsPreserved(before.items, restored.items) || restored.total_price > before.total_price) throw new Error('uncertain');
    throw new Error('restored');
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { eligible, codes, verified, accept };
  if (typeof document === 'undefined') return;
  const route = globalThis.Shopify?.routes?.root || '/';
  let opening = false;
  let bypass = false;

  async function request(endpoint, payload) {
    const options = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (payload) Object.assign(options, { method: 'POST', headers: { ...options.headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const response = await fetch(route + endpoint, options);
    if (!response.ok) throw new Error('uncertain');
    return response.json();
  }

  async function open(form, submitter, template) {
    opening = true;
    submitter.disabled = true;
    const config = { tagProduct: Number(template.dataset.tagProduct), loopProduct: Number(template.dataset.loopProduct), code: template.dataset.discountCode };
    function continueCheckout() {
      bypass = true;
      submitter.disabled = false;
      form.requestSubmit(submitter);
    }
    try {
      const cart = await request('cart.js');
      if (!eligible(cart, config)) { continueCheckout(); return; }
    } catch {
      continueCheckout();
      return;
    } finally { opening = false; submitter.disabled = false; }

    const dialog = template.content.querySelector('dialog').cloneNode(true);
    if (!dialog.querySelector('[data-checkout-offer-accept]')) { continueCheckout(); return; }
    const status = dialog.querySelector('[data-checkout-offer-status]');
    const controls = [...dialog.querySelectorAll('button,select')];
    let busy = false;
    function close() { dialog.close(); dialog.remove(); submitter.focus(); }
    dialog.addEventListener('cancel', event => { event.preventDefault(); if (!busy) close(); });
    dialog.addEventListener('click', async event => {
      if (busy) return;
      if (event.target.closest('[data-checkout-offer-close]')) { close(); return; }
      if (event.target.closest('[data-checkout-offer-skip]')) { close(); continueCheckout(); return; }
      if (!event.target.closest('[data-checkout-offer-accept]')) return;
      busy = true;
      controls.forEach(control => { control.disabled = true; });
      dialog.setAttribute('aria-busy', 'true');
      const variantId = Number(dialog.querySelector('[data-cart-offer-variant]').value);
      try {
        await accept(request, config, variantId);
        location.assign(route + 'checkout');
      } catch (error) {
        const restored = error.message === 'restored';
        const recovery = dialog.querySelector('[data-checkout-offer-recovery]');
        status.textContent = restored ? dialog.dataset.restored : dialog.dataset.error;
        recovery.hidden = false;
        dialog.removeAttribute('aria-busy');
        if (restored) {
          busy = false;
          dialog.querySelector('[data-checkout-offer-close]').disabled = false;
          const skip = dialog.querySelector('[data-checkout-offer-skip]');
          skip.disabled = false;
          skip.focus();
        } else {
          recovery.focus();
        }
      }
    });
    document.body.append(dialog);
    dialog.showModal();
  }

  document.addEventListener('submit', event => {
    if (event.defaultPrevented || bypass || event.submitter?.name !== 'checkout') return;
    const form = event.target.closest('[data-origin-cart]');
    const template = form?.querySelector('template[data-checkout-offer]');
    if (!template?.dataset.discountCode || event.submitter.disabled) return;
    event.preventDefault();
    if (!opening) open(form, event.submitter, template);
  });
})();